import React, { useLayoutEffect, useMemo, useState, useEffect } from "react";
import {
    useConnectedOrganisationsQuery,
    useActionsQuery,
    useUserActionsQuery,
    useUserQuery,
} from "../../../pages/wp-connect/queries";
import {
    Alert,
    Button,
    Container,
    Card,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
} from "reactstrap";
import { Trans, useTranslation } from "react-i18next";
import { chain } from "lodash";
import marnLogo from "../../../assets/images/applications/marn.svg";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { FormGroup, Select, SubmitButton, Input, DatePicker } from "components/react-hook-form";
import { useClient, useConnector, useModal, useOrganisationContext } from "hooks";
import { useQueryClient } from "react-query";
import { NotificationManager } from "react-notifications";
import { withSelectedOrganisation } from "components/withSelectedOrganisation";
import momentTZ from "moment-timezone";
import BackButton from "components/refactoring/BackButton";
import { Page } from "components/common";
import { useHistory } from "react-router-dom";


const TriggerSetup = ({
    value,
    selectedOrganisation,
    connectedOrganisations,
}) => {
    const { integration_label, is_zid, is_marn, triggers } = value;
    const client = useClient();
    const { t } = useTranslation();

    const methods = useForm({
        defaultValues: {
            connected_organisation: selectedOrganisation,
        },
        mode: "all",
    });
    const { watch, setValue, formState, reset, handleSubmit } = methods;
    const queryClient = useQueryClient();
    const { isValid } = formState;
    const [trigger, action] = watch(["trigger", "action"]);
    const actions = trigger?.actions ?? [];
    const { Modal, setIsOpen, isOpen } = useModal();

    useLayoutEffect(() => {
        setValue("action", null);
    }, [trigger, setValue]);

    async function onMarnSubmit({
        action,
        connected_organisation,
        trigger,
        ...rest
    }) {
        var payload = { ...rest };

        const objectsToFilter = [payload.marn_config]; // payload.marn_config.payment_types];
        objectsToFilter.forEach((element, index) => {
            for (const [key, value] of Object.entries(element)) {
                if (
                    (key.includes("POS") || key.includes("inventory") || index === 1) &&
                    value &&
                    value.id
                ) {
                    if (index === 1) {
                        payload.marn_config.payment_types[key] = value.id;
                    } else {
                        payload.marn_config[key] = value.id;
                    }
                } else if (
                    (key.includes("minutes") ||
                        key.includes("frequency") ||
                        key.includes("hours") ||
                        key.includes("timezone")) &&
                    value &&
                    value.Name
                ) {
                    payload.marn_config[key] = value.Name;
                } else if (key.includes("vat") && !key.includes("value")) {
                    payload.marn_config[key] = {
                        TaxRate: value.DisplayTaxRate,
                        TaxType: value.TaxType,
                    };
                } else if (key.includes("POS_TAX")) {
                    payload.marn_config[key] = value.Name;
                }
            }
        });

        console.log("payload:", payload);
        if (!payload.marn_config.suppliers) {
            payload.marn_config.suppliers = [];
        }

        client
            .post("user_wp_connect_actions", {
                action: action.id,
                connected_organisation: connected_organisation.id,
                ...payload,
            })
            .then((data) => {
                setIsOpen(false);

                queryClient
                    .refetchQueries("user_wp_connect_actions")
                    .then((data) => { })
                    .catch((ex) => {
                        console.error("Error: ", ex);
                    });

                reset();
                NotificationManager.success(t("Added successfully"));
            })
            .catch((ex) => {
                const code = ex?.response?.data?.code;
                if (code === "duplicate_action") {
                    NotificationManager.error(t("This action has already been set up"));
                    return;
                } else {
                    throw ex;
                }
            });
    }

    return (
        <FormProvider {...methods}>
            <tr>
                <td style={{ width: 1 }}>
                    {is_marn ? <MarnLogo /> : null}
                </td>
                <td
                    style={{ width: 1 }}
                    className="text-nowrap font-weight-semibold align-middle px-2"
                >
                    {t(integration_label)}
                </td>
                <td>
                    <FormGroup name="trigger" className="mb-0">
                        <Select
                            name="trigger"
                            placeholder={t("Select trigger...")}
                            options={triggers}
                            rules={{
                                required: true,
                            }}
                            labelKey="label"
                            valueKey="key"
                        />
                    </FormGroup>
                </td>
                <td style={{ width: 1 }} className="align-middle">
                    <span className="fa fa-angle-right fa-lg opacity-50" />
                </td>
                <td>
                    <FormGroup name="action" className="mb-0">
                        <Select
                            name="action"
                            placeholder={t("Select action...")}
                            options={actions}
                            rules={{
                                required: true,
                            }}
                            labelKey="type_label"
                            valueKey="id"
                        />
                    </FormGroup>
                </td>
                <td style={{ width: 1 }}>
                    {isOpen ? (
                        <Modal size="lg">
                            {/* {is_zid ? (
                <ZidTriggerSetup
                  onSubmit={handleSubmit(onZidSubmit)}
                  setIsOpen={setIsOpen}
                  action={action}
                  connectedOrganisations={connectedOrganisations}
                />
              ) : null} */}
                            {is_marn ? (
                                <MarnTriggerSetup
                                    onSubmit={handleSubmit(onMarnSubmit)}
                                    setIsOpen={setIsOpen}
                                    action={action}
                                    connectedOrganisations={connectedOrganisations}
                                />
                            ) : null}
                        </Modal>
                    ) : null}
                    <Button
                        size="sm"
                        color="primary"
                        disabled={!isValid}
                        onClick={() => setIsOpen(true)}
                    >
                        <Trans>Setup</Trans>
                    </Button>
                </td>
            </tr>
        </FormProvider>
    );
};


const MarnTriggerSetup = ({
    connectedOrganisations,
    setIsOpen,
    onSubmit,
    action,
}) => {
    const { t } = useTranslation();
    const { register, watch, setValue, unregister } = useFormContext();

    const client = useClient();

    const [locations, setLocations] = useState([]);
    const [paymentTypes, setPaymentTypes] = useState([]);

    const [paymentTypesList, setPaymentTypesList] = useState({});
    const [taxRates, setTaxRates] = useState([]);
    // const [trackingCategories, setTrackingCategories] = useState([]);

    const [showFullForm, setShowFullForm] = useState(false);

    const [connected_organisation, brand_key_form] = watch([
        "connected_organisation",
        "marn_config.brand_key",
    ]);
    const [branchId, setBranchId] = useState("");

    // hide the full form and reset brand key if organisation is changed
    useEffect(() => {
        setShowFullForm(false);
        setValue("marn_config.brand_key", "");
        if (connected_organisation) {
            setValue("marn_config.company_name", connected_organisation.name);
            setValue("marn_config.company_id", connected_organisation.id);
            requestFormData();
        }
    }, [connected_organisation]);

    // TODO Watches all form items for now just for quickness (might not need to fix really)
    watch();

    // Hide the full form if the brand key is edited
    useEffect(() => {
        if (showFullForm) {
            setShowFullForm(false);
        }
    }, [brand_key_form]);

    function refetchTaxRates() {
        client
            .get(`xero/${connected_organisation?.id}/taxrates`)
            .then((data) => {
                data["data"].forEach((taxRate, index) => {
                    const toAdd =
                        taxRate["Name"] + ": " + taxRate["DisplayTaxRate"] + "%";
                    data["data"][index]["Name"] = toAdd;
                });
                console.log("tax rates:", data);
                setTaxRates(data["data"]);
            })
            .catch((e) => {
                console.error("ERROR: ", e.response.data);
            });
    }

    function refetchAccounts() {
        client
            .get(`xero/${connected_organisation?.id}/accounts`)
            .then((data) => {
                const acceptableClasses = [
                    "REVENUE",
                    "LIABILITY",
                    "EQUITY",
                    "ASSET",
                    "EXPENSE",
                    "BANK",
                    "INVENTORY",
                    "DIRECTCOSTS",
                    "CURRENT",
                    "CURRLIAB",
                ];
                var accountResult = {
                    REVENUE: [],
                    LIABILITY: [],
                    EQUITY: [],
                    ASSET: [],
                    EXPENSE: [],
                    BANK: [],
                    INVENTORY: [],
                    DIRECTCOSTS: [],
                    CURRENT: [],
                    CURRLIAB: [],
                    OTHER: [],
                };

                const acceptableSystemTypes = [
                    "",
                    "GST",
                    "HISTORICAL",
                    "ROUNDING",
                    "RETAINEDEARNINGS",
                    "REALISEDCURRENCYGAIN",
                ];
                data["data"].forEach((account, index) => {
                    if (
                        acceptableClasses.includes(account.Type) &&
                        (!account.SystemAccount ||
                            acceptableSystemTypes.includes(account.SystemAccount))
                    ) {
                        accountResult[account.Type].push({
                            Name: account.Name + ": " + account.Code,
                            id: account["Code"],
                            account_id: account["AccountID"],
                            allow_payments:
                                account.Type === "BANK" || account.EnablePaymentsToAccount,
                        });
                    } else {
                        accountResult["OTHER"].push({
                            Name: account.Name + ": " + account.Code,
                            id: account["Code"],
                            type: account.Type,
                            account_id: account["AccountID"],
                            allow_payments:
                                account.Type === "BANK" || account.EnablePaymentsToAccount,
                        });
                    }
                });
                setPaymentTypesList(accountResult);
            })
            .catch((e) => {
                if (e.response && e.response.data) {
                    console.error("ERROR: ", e.response.data);
                } else {
                    console.error("ERROR: ", e);
                }
            });
    }

    function refetchTrackingCategories() {
        client
            .get(`xero/${connected_organisation?.id}/trackingcategories`)
            .then((data) => {
                data["data"].forEach((cat, index) => {
                    if (cat.Name === "Branches") {
                        setBranchId(cat.TrackingCategoryID);
                    }
                });
            })
            .catch((e) => {
                console.error("ERROR: ", e.response.data);
            });
    }

    function addOneNewBranch(branchName) {
        // console.log('LOCATIONS TO ADD: ', [...locationsToAdd]);
        client
            .put(
                `/xero/${connected_organisation?.id}/trackingcategories/${branchId}/options`,
                { Name: branchName, Status: "ACTIVE" }
            )
            .then((data) => {
                getLocations(brand_key_form.trim());
            })
            .catch((e) => {
                console.error("ERROR: ", e.response.data);
            });
    }

    function requestFormData() {
        unregister("supplier");

        refetchTaxRates();
        refetchAccounts();
        refetchTrackingCategories();
    }

    async function getLocations(brandKey) {
        setLocations([]);
        var locationsTemp = [];
        client
            .get("marn_helpers/get_locations", {
                params: {
                    org_id: connected_organisation.id,
                    brand_key: brandKey.trim(),
                },
            })
            .then((resp) => {
                for (let i = 0; i < resp.data.length; i++) {
                    const element = resp.data[i];
                    element["Name"] = element["LocationName"];
                    locationsTemp.push(element);
                }
                if (locationsTemp.length > 0) {
                    setShowFullForm(true);
                } else {
                    NotificationManager.error(
                        t("No locations available for this brand key")
                    );
                }
                setLocations([...locationsTemp]);
                refetchTrackingCategories();
            })
            .catch((e) => {
                console.log(e.response);
                // if (e.response.data.ResponseMessages.length > 0) {
                //   console.warn("ERROR RESP", e.response.data.ResponseMessages[0]);
                //   NotificationManager.error(e.response.data.ResponseMessages[0]);
                // }
            });
    }

    async function getMarnPaymentTypes(brandKey) {
        setPaymentTypes([]);
        var paymentTypes = [];
        client
            .get("marn_helpers/get_payment_types", {
                params: { brand_key: brandKey },
            })
            .then((resp) => {
                for (let i = 0; i < resp.data.length; i++) {
                    const element = resp.data[i];
                    // element['Name'] = element['Name'];
                    paymentTypes.push(element);
                }
                setPaymentTypes([...paymentTypes]);
            })
            .catch((e) => {
                if (
                    e &&
                    e.response &&
                    e.response.data &&
                    e.response.data.ResponseMessages &&
                    e.response.data.ResponseMessages.length > 0
                ) {
                    console.warn("ERROR RESP", e.response.data.ResponseMessages[0]);
                    NotificationManager.error(e.response.data.ResponseMessages[0]);
                }
            });
    }

    async function getMarnSuppliers(brandKey) {
        setPaymentTypes([]);
        var suppliers = [];
        // var suppliersForm = [];
        client
            .get("marn_helpers/get_suppliers", { params: { brand_key: brandKey } })
            .then((resp) => {
                if (!resp.data.length) {
                    setValue("marn_config.suppliers", []);
                } else {
                    for (let i = 0; i < resp.data.length; i++) {
                        const element = resp.data[i];
                        // element['Name'] = element['Name'];
                        suppliers.push(element);
                        setValue("marn_config.suppliers." + i + ".marn_location_option", {
                            Name: element["SupplierName"],
                            id: i,
                        });
                    }
                }
            })
            .catch((e) => {
                if (
                    e &&
                    e.response &&
                    e.response.data &&
                    e.response.data.ResponseMessages &&
                    e.response.data.ResponseMessages.length > 0
                ) {
                    console.warn("ERROR RESP", e.response.data.ResponseMessages[0]);
                    NotificationManager.error(e.response.data.ResponseMessages[0]);
                }
            });
    }

    function showLocations() {
        var locationsList = [];

        for (let i = 0; i < locations.length; i++) {
            const location = locations[i];
            if (location["Name"]) {
                locationsList.push(
                    createToggleItem(
                        "locations",
                        location["Name"],
                        location["LocationKey"],
                        location["inSystem"]
                    )
                );
            }
        }

        return locationsList.length > 0 ? (
            locationsList
        ) : (
            <h5> {t("No locations found")}</h5>
        );
    }

    function createToggleItem(formSection, name, Id = "", inSystem = false) {
        // const uniqueName = nameFormatted + '_' + Id;

        const formName = "marn_config." + formSection + "." + Id;
        return (
            <>
                <div className="col-md-6">
                    <div
                        key={"locationToggleContainer" + Id}
                        className="custom-control custom-switch"
                    >
                        <input
                            type="checkbox"
                            {...register(formName, {
                                value: inSystem,
                            })}
                            name={formName}
                            className="custom-control-input  pull-right"
                            id={Id}
                            onChange={(e) => {
                                if (!inSystem) {
                                    addOneNewBranch(name);
                                }
                            }}
                        />

                        <label className="custom-control-label" htmlFor={Id}>
                            <Trans>{name}</Trans>
                        </label>
                    </div>
                </div>
            </>
        );
    }

    function showPOS() {
        // const uniquePaymentTypes = [
        //   ...(paymentTypesList["CURRLIAB"] ?? []),
        //   ...(paymentTypesList["CURRENT"] ?? []),
        // ];
        var uniquePaymentTypesRevenue = paymentTypesList["REVENUE"] ?? [];

        return (
            <>
                <Container>
                    <Row>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="POS_sales"
                                labelText={t("Sales")}
                                options={uniquePaymentTypesRevenue}
                            />
                        </Col>
                        {/* <Col>
              <DropdownItemMarnForm
                uniqueName="POS_discount"
                labelText={t("Discount")}
                options={uniquePaymentTypesRevenue}
              />
            </Col> */}
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="POS_surcharges"
                                labelText={t("Surcharges")}
                                options={uniquePaymentTypesRevenue}
                            />
                        </Col>
                    </Row>
                    <br />
                    <Row>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="POS_tips"
                                labelText={t("Tips")}
                                options={uniquePaymentTypesRevenue}
                            />
                        </Col>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="POS_cash_rounding"
                                labelText={t("Cash rounding")}
                                options={uniquePaymentTypesRevenue}
                            />
                            {/* <DropdownItemMarnForm uniqueName='POS_loyalty' labelText={t('Loyalty')} options={uniquePaymentTypes} /> */}
                        </Col>
                    </Row>
                    <br />
                    <Row>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="POS_gift_card"
                                labelText={t("Gift Cards")}
                                options={uniquePaymentTypesRevenue}
                            />
                        </Col>
                        <Col></Col>
                        {/* <Col>
						<DropdownItemMarnForm uniqueName='POS_cash_rounding' labelText={t('Cash rounding')} options={uniquePaymentTypes} />
					</Col> */}
                    </Row>
                    <br />
                    {/* <Row>
					<Col>
						<DropdownItemMarnForm uniqueName='POS_adjustment' labelText={t('Adjustment')} options={uniquePaymentTypes} />
					</Col>
					<Col></Col>
				</Row>
				<br /> */}
                </Container>
            </>
        );
    }

    function showInventoryAccounts() {
        const uniquePaymentTypes = [
            ...(paymentTypesList["ASSET"] ?? []),
            ...(paymentTypesList["DIRECTCOSTS"] ?? []),
            ...(paymentTypesList["CURRENT"] ?? []),
        ];

        const uniquePaymentTypesExpense = [
            ...(paymentTypesList["DIRECTCOSTS"] ?? []),
            ...(paymentTypesList["EXPENSE"] ?? []),
        ];
        return (
            <>
                <Container>
                    <Row>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="inventory_inventory"
                                labelText={t("Inventory")}
                                options={uniquePaymentTypes}
                            />{" "}
                        </Col>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="inventory_cost_of_goods_sold"
                                labelText={t("Cost of goods sold")}
                                options={uniquePaymentTypesExpense}
                            />{" "}
                        </Col>
                    </Row>
                    <br />
                    <Row>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="inventory_inventory_adjustment"
                                labelText={t("Inventory adjustment")}
                                options={uniquePaymentTypes}
                            />{" "}
                        </Col>
                        <Col>
                            <DropdownItemMarnForm
                                uniqueName="inventory_pending_transfer"
                                labelText={t("Pending transfer")}
                                options={uniquePaymentTypes}
                            />{" "}
                        </Col>
                    </Row>
                    <br />
                </Container>
            </>
        );
    }

    function showPaymentTypes() {
        const elementsToShow = [];
        const paymentsToShow = [
            ...(paymentTypesList["BANK"] ?? []),
            ...(paymentTypesList["CURRLIAB"] ?? []),
            ...(paymentTypesList["CURRENT"] ?? []),
        ].filter((i) => i.allow_payments);
        paymentTypes.forEach((type, index) => {
            elementsToShow.push(
                <Col>
                    <div key={"supplier_" + index}>
                        <Row>
                            <Col>
                                <DropdownItemMarnForm
                                    uniqueName={"payment_types." + type.PaymentTypeName}
                                    labelText={t(type.PaymentTypeName)}
                                    options={paymentsToShow}
                                />
                            </Col>
                        </Row>
                        <br />
                    </div>
                </Col>
            );
        });

        return <>{elementsToShow}</>;
    }

    function showTaxTypes() {
        return (
            <>
                <Row>
                    <Col>
                        <Trans>
                            <p className="font-weight-bold">
                                {" "}
                                Please ensure this TAX values are correct for your xero
                                organisation{" "}
                            </p>
                        </Trans>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {/* <NumberItemMarnForm t={t} uniqueName='vat_value' min='0' max='100' useFormContext={useFormContext} labelText={t('VAT Percent (%)')} /> */}
                        <DropdownItemMarnForm
                            uniqueName="vat_on_sales"
                            labelText={t("Sales")}
                            options={taxRates}
                        />
                    </Col>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="vat_on_surcharges"
                            labelText={t("Surcharges")}
                            options={taxRates}
                        />

                        {/* <DropdownItemMarnForm uniqueName='vat_on_purchase' labelText={t('Purchase')} options={taxRates} /> */}
                    </Col>
                </Row>
                <br />
                <Row>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="vat_on_tips"
                            labelText={t("Tips")}
                            options={taxRates}
                        />{" "}
                    </Col>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="vat_on_cash_rounding"
                            labelText={t("Cash rounding")}
                            options={taxRates}
                        />{" "}
                    </Col>
                </Row>
                <br />
                <Row>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="vat_on_gift_cards"
                            labelText={t("Gift Cards")}
                            options={taxRates}
                        />{" "}
                    </Col>
                    <Col>
                        {/* <NumberItemMarnForm t={t} uniqueName='vat_value' min='0' max='100' useFormContext={useFormContext} labelText={t('VAT Percent (%)')} /> */}
                        <DropdownItemMarnForm
                            uniqueName="vat_on_purchases"
                            labelText={t("Purchases")}
                            options={taxRates}
                        />
                    </Col>
                </Row>
                <br />
            </>
        );
    }

    function showScheduler() {
        const dailyWeeklyOptions = [
            { Name: "Daily", id: 1 },
            { Name: "Weekly", id: 2 },
        ];

        const hours = [];
        for (let i = 0; i < 24; i++) {
            const element = { id: i.toString(), Name: i.toString() };
            hours.push(element);
        }

        const minutes = [
            { Name: "00", id: 1 },
            { Name: "15", id: 2 },
            { Name: "30", id: 3 },
            { Name: "45", id: 4 },
        ];

        const timeZonesList = momentTZ.tz.names();

        timeZonesList.forEach((element, index) => {
            timeZonesList[index] = { Name: element, id: index };
        });

        return (
            <>
                <Row>
                    <Col>
                        <Trans>
                            <p className="font-weight-bold">
                                {" "}
                                Remember to schedule this after the POS system closes{" "}
                            </p>
                        </Trans>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="frequency"
                            labelText={t("Frequency")}
                            options={dailyWeeklyOptions}
                        />
                    </Col>
                    <Col>
                        <label>
                            <Trans>Start sync data from (Date & Time):</Trans>
                        </label>
                        <DatePicker
                            defaultValue={() => {
                                var date = new Date();
                                const offset = date.getTimezoneOffset();
                                date = new Date(date.getTime() - offset * 60 * 1000);
                                return date.toISOString().split("T")[0];
                            }}
                            className="form-control"
                            name="marn_config.Date"
                            rules={{ required: true }}
                        />
                    </Col>
                </Row>
                <br />
                <Row>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="hours"
                            labelText={t("Hour")}
                            options={hours}
                        />
                    </Col>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="minutes"
                            labelText={t("Minute")}
                            options={minutes}
                        />
                    </Col>
                </Row>
                <br />
                <Row>
                    <Col>
                        <DropdownItemMarnForm
                            uniqueName="timezone"
                            labelText={t("Timezone")}
                            options={timeZonesList}
                        />
                    </Col>
                    <Col>
                        <label>
                            <Trans>Get From Previous Dates:</Trans>
                        </label>
                        <DatePicker
                            defaultValue={() => {
                                var date = new Date();
                                const offset = date.getTimezoneOffset();
                                date = new Date(date.getTime() - offset * 60 * 1000);
                                return date.toISOString().split("T")[0];
                            }}
                            className="form-control"
                            name="marn_config.backdate"
                            rules={{ required: true }}
                        />
                    </Col>
                </Row>
            </>
        );
    }

    return (
        <>
            <ModalHeader>
                <Trans>Setup Marn Trigger</Trans>
            </ModalHeader>
            <ModalBody>
                <FormHeading label={"Organisation & Brand Key"} />
                <FormGroup
                    label={t("Hesaab organisation")}
                    name="connected_organisation"
                >
                    <Select
                        options={connectedOrganisations}
                        labelKey="name"
                        valueKey="id"
                        rules={{ required: true }}
                        name="connected_organisation"
                    />
                </FormGroup>

                <FormGroup label={t("Brand key")} name="marn_config.brand_key">
                    <Input
                        shouldUnregister
                        type="text"
                        className="form-control"
                        name="marn_config.brand_key"
                        rules={{
                            required: true,
                        }}
                    />

                    {showFullForm ? null : (
                        <>
                            <br />
                            <Button
                                onClick={() => {
                                    getLocations(brand_key_form.trim());
                                    getMarnPaymentTypes(brand_key_form.trim());
                                    getMarnSuppliers(brand_key_form.trim());
                                    requestFormData();
                                }}
                            >
                                <Trans>Submit Brand Key</Trans>
                            </Button>
                        </>
                    )}
                </FormGroup>
                {showFullForm ? (
                    <>
                        <FormGroup label={t("Business Name")}>
                            <Input
                                shouldUnregister
                                type="text"
                                className="form-control"
                                name="marn_config.brand_name"
                                rules={{
                                    required: true,
                                }}
                            />
                        </FormGroup>
                        <FormGroup name="synced_locations">
                            <FormHeading label={"Marn Locations To Sync"} />
                            <div className="row">{showLocations()}</div>

                            <br />
                        </FormGroup>
                        <FormGroup name="map_pos_transactions">
                            <FormHeading label={"Map POS Transactions"} />
                            {showPOS()}
                        </FormGroup>
                        <FormGroup name="inventory_accounts">
                            <FormHeading label={"Inventory Accounts"} />
                            {showInventoryAccounts()}
                        </FormGroup>

                        <FormGroup name="map_payment_types">
                            <FormHeading label={"Map Payment types"} />
                            {showPaymentTypes()}
                            {/* <ShowListComponent labelPrefix='payment_type' options={paymentTypesList} t={t} /> */}
                        </FormGroup>
                        <FormGroup name="map_tax_types">
                            <FormHeading label={"Map Tax types"} />
                            {showTaxTypes()}
                        </FormGroup>
                        <FormGroup name="sync_scheduler">
                            <FormHeading label={"Sync Scheduler"} />
                            {showScheduler()}
                        </FormGroup>
                    </>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="link"
                    onClick={() => {
                        setIsOpen(false);
                    }}
                >
                    <Trans>Close</Trans>
                </Button>
                {showFullForm ? (
                    <SubmitButton
                        onClick={() => {
                            onSubmit();
                        }}
                    >
                        <Trans>Create</Trans>
                    </SubmitButton>
                ) : null}
            </ModalFooter>
        </>
    );
};


const MarnLogo = () => {
    const { t } = useTranslation();
    return (
        <img
            alt={t("Marn logo")}
            src={marnLogo}
            style={{ width: "1.2rem", height: "1.2rem" }}
        />
    );
};

const ConfiguredIntegration = ({ value, onDelete }) => {
    const { action } = value;
    const { t } = useTranslation();
    const methods = useForm({});
    const { handleSubmit } = methods;

    return (
        <FormProvider {...methods}>
            <tr className="align-middle">
                <td style={{ width: 1 }}>
                    {action.is_marn ? <MarnLogo /> : null}
                </td>
                <td
                    style={{ width: 1 }}
                    className="text-nowrap font-weight-semibold px-2"
                >
                    {t(action.integration_label)}
                </td>
                <td>
                    {t(action.trigger_label)}
                    {!!value.marn_config && (
                        <>
                            {" "}
                            At {t(value.marn_config.hours + ":" + value.marn_config.minutes)}
                        </>
                    )}
                </td>
                <td>{value.marn_config ? value.marn_config.brand_name : null}</td>
                <td>{t(action.type_label)}</td>
                <td>{value.connected_organisation__name}</td>
                <td style={{ width: 1 }}>
                    <SubmitButton
                        color="link"
                        size="sm"
                        className="text-danger"
                        onClick={handleSubmit(onDelete)}
                    >
                        <span className="fa fa-trash" />
                    </SubmitButton>
                </td>
            </tr>
        </FormProvider>
    );
};


export const ConfiguredIntegrations = ({ v, app, store_logo }) => {
    const { data, refetch } = useUserActionsQuery();
    const { t } = useTranslation();
    const client = useClient();

    if (!data) return null;

    const onDelete = (v) => async () => {
        await client.delete(`user_wp_connect_actions/${v.id}`);
        await refetch();
        NotificationManager.success(t("Removed successfully"));
    };

    return (
        <div className="inv-table">
            <Card body>
                <h4 className="">
                    <Trans>Configured integrations</Trans>
                </h4>
                <table className="table mb-0">
                    <thead>
                        <tr>
                            <th />
                            <th />
                            <th>
                                <Trans>Trigger</Trans>
                            </th>
                            <th>
                                <Trans>Business Name</Trans>
                            </th>
                            <th>
                                <Trans>Action</Trans>
                            </th>
                            <th>
                                <Trans>Organisation</Trans>
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {!data.length ? (
                            <tr>
                                <td colSpan={999}>
                                    <Alert color="info" className="text-center mb-0">
                                        <Trans>
                                            You have not configured any WP Connect integrations yet.
                                        </Trans>


                                    </Alert>
                                </td>
                            </tr>
                        ) : null}
                        {data.map((i) => (
                            <ConfiguredIntegration
                                value={i}
                                key={i.id}
                                onDelete={onDelete(i)}
                            />
                        ))}
                    </tbody>
                </table>
            </Card>
        </div>
    );
};

const _AvailableIntegrations = ({ selectedOrganisation }) => {
    const { t } = useTranslation();

    const { data: connectedOrganisations } = useConnectedOrganisationsQuery({
        select: (v) => v.filter((i) => i.status !== "NOT_CONNECTED"),
    });

    const [connector, setConnector] = useState(null)
    const connectClient = useConnector()
    const removeAccount = async () => {
        await connectClient.delete('user/' + selectedOrganisation?.xero_organisation_id);
        setConnector(null);
    }
    const getData = async () => {
        if (selectedOrganisation && selectedOrganisation.status === 'LIVE') {
            const { data: _connector } = await connectClient(`user/${selectedOrganisation.xero_organisation_id}`);
            if (_connector && _connector.data) setConnector(_connector.data);
            else setConnector(null)

        } else {
            setConnector(null)
        }

    }
    useEffect(() => {
        (async () => {
            await getData()
        })()
    }, [selectedOrganisation]);

    const { data: _integrations } = useActionsQuery({
        select: (data) =>
            chain(data)
                .groupBy("integration")
                .reduce((acc, v) => {
                    const first = v[0];

                    acc.push({
                        integration: first.integration,
                        integration_label: first.integration_label,
                        is_zid: first.is_zid,
                        is_marn: first.is_marn,
                        triggers: chain(v)
                            .groupBy("trigger")
                            .map((i) => ({
                                key: i[0].trigger,
                                label: i[0].trigger_label,
                                actions: i,
                            }))
                            .value(),
                    });

                    return acc;
                }, [])
                .value(),
        placeholderData: [],
    });

    const integrations = useMemo(
        () =>
            _integrations.map((i) => ({
                ...i,
                triggers: i.triggers.map((j) => ({
                    ...j,
                    label: t(j.label),
                    actions: j.actions.map((k) => ({
                        ...k,
                        type_label: t(k.type_label),
                    })),
                })),
            })),
        [_integrations, t]
    );

    return (
        <div>
            <Card body>
                <h4 className="mb-4"><Trans>Select Marn Integration</Trans></h4>
                <table className="table table-sm table-borderless mb-0">
                    <tbody>
                        {integrations.map((i) => (
                            <TriggerSetup
                                selectedOrganisation={selectedOrganisation}
                                connectedOrganisations={connectedOrganisations}
                                value={i}
                                key={i.integration}
                            />
                        ))}
                    </tbody>
                </table>
            </Card>
        </div>
    );
};


export const AvailableIntegrations = withSelectedOrganisation()(
    _AvailableIntegrations
);

export const Home = () => {
    const { data: user, isLoading } = useUserQuery({
        keepPreviousData: true,
    });

    const history = useHistory()

    const { selectedOrganisation } = useOrganisationContext()

    if (isLoading) return null;

    const container = (children) => (
        <Container className="mt-4">{children}</Container>
    );

    const goToCustomizeSubscriptionPage = (from) => {
        history.push({
            pathname: '/feature-subscription',
            search: `from=${from}`
        })
    }

    const isZatcaSubscribed = selectedOrganisation?.subscribed_features?.hesaabmodule?.includes('wpconnect')
    const isTrial = selectedOrganisation?.is_trial
    const isConnected = selectedOrganisation?.status === "LIVE"

    if (!isZatcaSubscribed && !isTrial) {
        return container(
            <>
                <Page title="Integration Page">
                    <BackButton />
                    <Alert color="warning">
                        <Trans>Your subscription does not allow access to E-Invoicing.</Trans>
                    </Alert>

                    <div className="text-center">
                        <Button size="sm" color="primary" onClick={() => goToCustomizeSubscriptionPage('wpconnect')}><Trans>Buy Now</Trans></Button>
                    </div>

                </Page>
            </>
        );
    } else if (isTrial && !isZatcaSubscribed) {
        return container(
            <Alert color="warning">
                <Trans>You cannot access E-Invoicing while in Trial Period.</Trans>
            </Alert>
        );
    } else if (!isConnected) {
        return container(
            <Alert color="warning">
                <Trans>Please connect your organisation to xero to utilize E-Invoicing.</Trans>
            </Alert>
        );
    }

    return container(
        <>
            <Page title="Marn Integration">
                <BackButton />
                <ConfiguredIntegrations />
                <AvailableIntegrations />
            </Page>
        </>
    );
};

// =================================================================================
// Some components to help with the marn form
// =================================================================================

const DropdownItemMarnForm = ({
    labelText,
    options,
    uniqueName,
    label = "Name",
    optionsState = null,
    setOptionsState = null,
}) => {
    var clonedOptions = [];

    if (options != null) {
        clonedOptions = options.map((a) => Object.assign({}, a));
    }

    return (
        <>
            <label>
                <Trans>{labelText}:</Trans>
            </label>
            <Select
                options={clonedOptions}
                labelKey={label}
                valueKey={label}
                name={"marn_config." + uniqueName}
                rules={{
                    required: true,
                }}
                shouldUnregister={true}
                defaultValue={
                    uniqueName.includes("tracking") || uniqueName.includes("supplier")
                        ? null
                        : clonedOptions[0]
                }
            // defaultValue={clonedOptions[0]}
            />
        </>
    );
};

const FormHeading = ({ label }) => {
    return (
        <>
            <h3>
                <Trans>{label}</Trans>
            </h3>
        </>
    );
};

